// A reusable Supabase-backed record store for Momentum's multi-instance tools.
//
// WHY THIS EXISTS
// Every Momentum tool that owns a list of things — offers, social posts, funnels,
// CTAs, talks — needs the same seven behaviours: load my rows, save one, delete
// one, know whether it's still loading, know whether it failed, generate an id,
// and translate between the app's camelCase and Postgres's snake_case. Written
// per tool that's seven chances to get it subtly wrong nineteen times over.
// `Sidebar.jsx` has referred to a shared "drafts store" since before this file
// existed; this is that store.
//
// WHAT IT DELIBERATELY DOES NOT DO
// No offline fallback and no localStorage mirror. A failed write surfaces as
// `error` and the row is NOT saved — one source of truth, chosen so there's never
// a question of which copy wins. Callers must show `error` to the user; swallowing
// it would silently lose her work, which is strictly worse than a visible failure.
//
// Per-tool logic (parsing locked Accelerator prose, provenance, resync) does NOT
// belong here — see `offerParse.js` for that half of the offer ladder.

import { useState, useEffect, useCallback, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { useSupabase } from "../supabaseClient";

// Postgres columns are snake_case, the app is camelCase. Deriving the mapping
// from the key name means adding a field needs no second edit — a hand-written
// map is one more place to forget, and a missed field reads as `undefined`
// rather than throwing, which is the silent-failure mode we're avoiding.
export const toSnake = (k) => k.replace(/[A-Z]/g, (c) => "_" + c.toLowerCase());
export const toCamel = (k) => k.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

/**
 * An id derived from the account rather than from randomness.
 *
 * For the ONE row a tool creates on its own — her first site, her first course.
 * A random id gives Postgres nothing to collide on, so two browsers open at the
 * same moment each insert one and she ends up with two "My site"s, which is
 * exactly the duplicate the offers pull needed a `seeded` flag to prevent. A
 * derived id makes the second insert an upsert of the same row.
 *
 * supabase/0015's backfill derives the same way (`'ste_' || substr(md5(...)`)
 * and for the same reason: run it twice, get one site. The hash here is not
 * md5 — there is no synchronous one in a browser — and it does not need to be:
 * an id is not a secret, and what is being asked of it is only "the same input
 * gives the same output". The `u` in front of the hash keeps it clear of the
 * backfill's ids, which are md5 hex and so never contain one. It never runs for
 * an account the backfill covered anyway, because that account has its row.
 */
export function derivedId(prefix, userId) {
  const s = String(userId || "");
  // FNV-1a, 32-bit. Short, stable, and no dependency.
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${prefix}_u${h.toString(36)}`;
}

/**
 * ONE LOAD, SHARED — for a table many screens read at once (the Library, her
 * call 2026-09-28: "it needs to lazy load", meaning it was slow to open). Every
 * hook instance used to load the whole table again: the Library screen, every
 * post she opened, every picker. With `shared`, the first one loads, the rest
 * reuse it — and an in-flight load is joined, not repeated — and a save or a
 * delete anywhere is published to every screen holding the list.
 *
 * Keyed by user: a different account never sees the last one's rows.
 * Pure (no React), so it is tested on its own.
 */
export function createSharedCache() {
  const c = { user: null, rows: null, promise: null, subs: new Set() };
  return {
    rowsFor: (user) => (c.user === user ? c.rows : null),
    subscribe(fn) { c.subs.add(fn); return () => c.subs.delete(fn); },
    publish(user, rows) {
      if (c.user !== user) return;
      c.rows = rows;
      c.subs.forEach((fn) => fn(rows));
    },
    /* A save before the first load has landed changes nothing here: marking
       the list loaded with one row in it would stop the real load happening. */
    update(user, fn) {
      if (c.user !== user || !c.rows) return;
      this.publish(user, fn(c.rows));
    },
    /* Loads once per user; `force` loads again (a refresh). Concurrent callers
       share one request. Resolves to the rows; rejects on failure. */
    load(user, fetcher, force = false) {
      if (c.user !== user) { c.user = user; c.rows = null; c.promise = null; }
      if (!force && c.rows) return Promise.resolve(c.rows);
      if (c.promise) return c.promise;
      const p = fetcher()
        .then((rows) => { if (c.promise === p) c.promise = null; this.publish(user, rows); return rows; })
        .catch((err) => { if (c.promise === p) c.promise = null; throw err; });
      c.promise = p;
      return p;
    },
  };
}

/**
 * Build a hook for one table.
 *
 * @param table    Postgres table name, e.g. "offers"
 * @param fields   camelCase field names the app owns. `id`, `createdAt` and
 *                 `updatedAt` are handled for you and must NOT be listed.
 * @param idPrefix short prefix for generated ids, e.g. "off" → "off_a1b2c3d4"
 * @param orderBy  column to sort by, newest first. Defaults to created_at.
 * @param ascending sort direction. Defaults to false (newest first), which is
 *                 right for every list she adds to. A child table ordered by
 *                 `sort_order` wants true — block 1 goes above block 2.
 * @param ownerColumn the column holding the Clerk user id. Pass `null` for a
 *                 CHILD table that has no such column because it belongs to its
 *                 parent row — `website_sections` is owned through `page_id`.
 *                 Then the filter is left to that table's RLS policy, which
 *                 walks the foreign key, and no owner column is written on save.
 *                 See the note on `load` before using this for a new table.
 */
export function createRecordStore({
  table, fields, idPrefix, orderBy = "created_at", ascending = false,
  ownerColumn = "clerk_user_id",
  /* ONE LOAD FOR EVERY SCREEN (createSharedCache). Only for tables many
     screens read at the same time; the rest keep a list per screen. */
  shared = false,
}) {
  const newId = () => `${idPrefix}_` + Math.random().toString(36).slice(2, 10);
  const cache = shared ? createSharedCache() : null;

  // Only ever send columns we own. A stray key (React's `isNew`, a stale field
  // from an older shape) would make Postgres reject the whole write.
  const toRow = (obj, clerkUserId) => {
    const row = { id: obj.id || newId() };
    if (ownerColumn) row[ownerColumn] = clerkUserId;
    for (const f of fields) {
      if (obj[f] !== undefined) row[toSnake(f)] = obj[f];
    }
    return row;
  };

  const fromRow = (row) => {
    const obj = {};
    for (const [k, v] of Object.entries(row || {})) {
      if (k === ownerColumn) continue;   // ownership is implicit, never in app state
      obj[toCamel(k)] = v;
    }
    return obj;
  };

  /* `refresh`: for a SHARED store, show what is already loaded at once and
     quietly load again behind it — the screen whose whole job is the list (the
     Library) wants that; a picker or a post screen does not. */
  return function useRecordStore({ refresh = false } = {}) {
    const { user } = useUser();
    const getSupabase = useSupabase();
    const cached = cache ? cache.rowsFor(user?.id) : null;
    const [rows, setOwnRows] = useState(cached || []);
    const [loading, setLoading] = useState(!cached);
    const [error, setError] = useState(null);

    // getSupabase is a fresh closure every render (it depends on the Clerk
    // session), so depending on it directly would re-fire the load effect
    // forever. The ref keeps the latest without being a dependency.
    const getSb = useRef(getSupabase);
    getSb.current = getSupabase;

    /* Every screen holding a shared list hears every change to it. */
    useEffect(() => (cache ? cache.subscribe(setOwnRows) : undefined), []);
    const setRows = useCallback((fn) => {
      if (cache && cache.rowsFor(user?.id)) cache.update(user?.id, fn);
      else setOwnRows(fn);
    }, [user?.id]);

    const fetchRows = useCallback(async () => {
      const sb = await getSb.current();
      /* A child table has no owner column to filter on, so the WHERE clause is
         its RLS policy walking the foreign key up to `clerk_user_id`. That is
         already the real boundary on every table here — the explicit `.eq`
         below is a second belt, not the only one — but it does mean a child
         table MUST have RLS enabled with a policy that joins to its parent, or
         this select returns everyone's rows. Check the migration before
         passing `ownerColumn: null` for a new table. */
      let q = sb.from(table).select("*");
      if (ownerColumn) q = q.eq(ownerColumn, user.id);
      const { data, error: e } = await q.order(orderBy, { ascending });
      if (e) throw e;
      return (data || []).map(fromRow);
    }, [user?.id]);

    const load = useCallback(async (force = true) => {
      if (!user?.id) return;
      const quiet = cache && !!cache.rowsFor(user.id);
      if (!quiet) setLoading(true);
      setError(null);
      try {
        if (cache) setOwnRows(await cache.load(user.id, fetchRows, force));
        else setOwnRows(await fetchRows());
      } catch (err) {
        console.error(`Failed to load ${table}:`, err);
        // Deliberately does not clear `rows` — a failed refresh shouldn't blank
        // a list she is looking at. The error is what tells her it's stale.
        setError(err.message || `Couldn't load your ${table}.`);
      } finally {
        setLoading(false);
      }
    }, [user?.id, fetchRows]);

    /* A shared list loads once per visit (or refreshes quietly, if asked);
       every other store loads on mount, as it always has. */
    const refreshRef = useRef(refresh);
    useEffect(() => { load(cache ? refreshRef.current : true); }, [load]);

    /**
     * Insert or update one record. Returns the saved object on success and
     * `null` on failure — callers MUST check, because on failure nothing was
     * written and `error` is set.
     */
    const save = useCallback(async (obj) => {
      if (!user?.id) return null;
      setError(null);
      try {
        const sb = await getSb.current();
        const row = toRow(obj, user.id);
        const { data, error: e } = await sb
          .from(table)
          .upsert(row, { onConflict: "id" })
          .select()
          .single();
        if (e) throw e;
        const saved = fromRow(data);
        // Replace in place if present, otherwise put it at the top so the list
        // matches the newest-first order the next load will produce.
        setRows((prev) => {
          const i = prev.findIndex((r) => r.id === saved.id);
          if (i < 0) return [saved, ...prev];
          const next = [...prev];
          next[i] = saved;
          return next;
        });
        return saved;
      } catch (err) {
        console.error(`Failed to save ${table}:`, err);
        setError(err.message || "Couldn't save. Check your connection and try again.");
        return null;
      }
    }, [user?.id]);

    /** Delete one record. Returns true on success, false on failure. */
    const remove = useCallback(async (id) => {
      if (!user?.id) return false;
      setError(null);
      try {
        const sb = await getSb.current();
        const { error: e } = await sb.from(table).delete().eq("id", id);
        if (e) throw e;
        setRows((prev) => prev.filter((r) => r.id !== id));
        return true;
      } catch (err) {
        console.error(`Failed to delete from ${table}:`, err);
        setError(err.message || "Couldn't delete. Check your connection and try again.");
        return false;
      }
    }, [user?.id]);

    return { rows, loading, error, save, remove, reload: load, clearError: () => setError(null), newId };
  };
}
